import React, { useEffect, useMemo, useState } from "react";
import {
  PROVIDERS,
  PROVIDER_CATEGORY_MAPPING,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
} from "../../config/constants";
import { apiService } from "../../services/apiService";
import {
  UploadCloudIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
} from "../icons";
import Spinner from "../ui/Spinner";
import Loading from "../ui/Loading";
import Toast from "../ui/Toast";
import ReplaceComparisonGroupFilter from "./ReplaceComparisonGroupFilter";

import CustomSelect from "../ui/CustomSelect";
export function ImportReplaceStepper({ step }) {
  const steps = [
    { id: 1, label: "Choose file" },
    { id: 2, label: "Preview" },
    { id: 3, label: "Import" },
  ];
  return (
    <div className="flex items-center gap-3 text-[13px]">
      {steps.map((s, idx) => {
        const active = step === s.id;
        const done = step > s.id;
        return (
          <React.Fragment key={s.id}>
            {idx > 0 && <div className="w-8 h-px bg-[var(--line,#e4e2dc)]" />}
            <div className="flex items-center gap-2">
              <span
                className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold ${
                  done
                    ? "bg-[var(--good,#1f6b34)] text-white"
                    : active
                      ? "bg-[var(--accent,#a51c1c)] text-white"
                      : "bg-[var(--sunken,#eeece7)] text-[var(--muted,#6b6a66)]"
                }`}
              >
                {done ? "✓" : s.id}
              </span>
              <span
                className={
                  active
                    ? "font-semibold text-[var(--accent,#a51c1c)]"
                    : done
                      ? "text-[var(--good,#1f6b34)]"
                      : "text-[var(--muted,#6b6a66)]"
                }
              >
                {s.label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function HeaderChipPanel({ title, tone = "gray", items = [] }) {
  const tones = {
    red: {
      box: "border-red-200 bg-red-50",
      title: "text-red-800",
      chip: "bg-white border-red-200 text-red-800",
      empty: "text-red-600",
    },
    green: {
      box: "border-green-200 bg-green-50",
      title: "text-green-800",
      chip: "bg-white border-green-200 text-green-800",
      empty: "text-green-700",
    },
    gray: {
      box: "border-gray-200 bg-gray-50",
      title: "text-gray-700",
      chip: "bg-white border-gray-200 text-gray-700",
      empty: "text-gray-500",
    },
  };
  const t = tones[tone] || tones.gray;
  const list = Array.isArray(items) ? items : [];

  return (
    <div className={`rounded-md border p-2.5 ${t.box}`}>
      <div className={`text-xs font-semibold mb-1.5 ${t.title}`}>
        {title}{" "}
        <span className="font-normal opacity-80">({list.length})</span>
      </div>
      {list.length === 0 ? (
        <p className={`text-xs ${t.empty}`}>None</p>
      ) : (
        <div className="max-h-24 overflow-y-auto flex flex-wrap gap-1">
          {list.map((h) => (
            <span
              key={h}
              className={`inline-flex px-1.5 py-0.5 rounded border text-[11px] leading-tight ${t.chip}`}
              title={h}
            >
              {h}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ImportReplaceWizard({
  tenantId,
  onBack,
  onPreviewReady,
  onComplete,
}) {
  const [step, setStep] = useState(1);
  const [provider, setProvider] = useState("");
  const [category, setCategory] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [formatInfo, setFormatInfo] = useState(null);
  const [formatLoading, setFormatLoading] = useState(false);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [replacing, setReplacing] = useState(false);
  const [showReplaceConfirmModal, setShowReplaceConfirmModal] = useState(false);
  const [replaceConfirmAcknowledged, setReplaceConfirmAcknowledged] =
    useState(false);
  const [toast, setToast] = useState({ message: null, type: "success" });

  const availableCategories = useMemo(() => {
    if (!provider) return [];
    return PROVIDER_CATEGORY_MAPPING[provider] || [];
  }, [provider]);

  const providerOptions = useMemo(() => {
    const fromMapping = Object.keys(PROVIDER_CATEGORY_MAPPING);
    return fromMapping.length ? fromMapping : PROVIDERS;
  }, []);

  // Load format info when provider+category selected
  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!provider || !category) {
        setFormatInfo(null);
        return;
      }
      setFormatLoading(true);
      try {
        const result = await apiService.getImportFormatInfo(provider, category);
        if (!cancelled) setFormatInfo(result?.data || null);
      } catch (err) {
        if (!cancelled) {
          setFormatInfo(null);
          setToast({
            message: err.message || "Could not load expected format",
            type: "error",
          });
        }
      } finally {
        if (!cancelled) setFormatLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [provider, category]);

  const clearFile = () => {
    setSelectedFile(null);
    setValidation(null);
  };

  const runValidation = async (file, nextProvider = provider, nextCategory = category) => {
    if (!file || !nextProvider || !nextCategory) return;
    setValidating(true);
    setValidation(null);
    try {
      const formData = new FormData();
      formData.append("provider", nextProvider);
      formData.append("category", nextCategory);
      formData.append("file", file);
      const result = await apiService.validateReplaceFile(formData);
      setValidation(result);
    } catch (err) {
      const payload = err.payload || {};
      setValidation({
        success: false,
        valid: false,
        message: err.message,
        data: payload.data || {
          headers: payload.details?.headers,
          blocking_issues:
            payload.details?.blocking_issues ||
            payload.data?.blocking_issues ||
            [],
          suggested_category: payload.data?.suggested_category,
        },
      });
    } finally {
      setValidating(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!provider || !category) {
      setToast({
        message: "Select a provider and category before uploading a file.",
        type: "error",
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      setValidation({
        success: false,
        valid: false,
        message: `File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`,
        data: {
          blocking_issues: [
            {
              code: "file_too_large",
              title: "File is too large",
              explanation: `This file is ${formatBytes(file.size)}. The limit is ${MAX_FILE_SIZE_MB}MB.`,
              how_to_fix: "Reduce the file size or split the data, then upload again.",
            },
          ],
          headers: { missing: [], matched: [], unused: [] },
        },
      });
      setSelectedFile(file);
      return;
    }

    setSelectedFile(file);
    await runValidation(file);
  };

  const handleSwitchCategory = async (suggested) => {
    if (!suggested || !selectedFile) return;
    setCategory(suggested);
    await runValidation(selectedFile, provider, suggested);
  };

  const handleDownloadTemplate = async () => {
    try {
      await apiService.downloadImportTemplate(provider, category, tenantId);
    } catch (err) {
      setToast({
        message: err.message || "Failed to download template",
        type: "error",
      });
    }
  };

  const handlePreview = async () => {
    if (!selectedFile || !validation?.valid) return;
    setPreviewLoading(true);
    try {
      const compareForm = new FormData();
      compareForm.append("provider", provider);
      compareForm.append("category", category);
      compareForm.append("file", selectedFile);
      const compareResult = await apiService.previewReplaceInventory(compareForm);

      const stageForm = new FormData();
      stageForm.append("provider", provider);
      stageForm.append("category", category);
      if (tenantId) stageForm.append("tenant_id", tenantId);
      stageForm.append("file", selectedFile);
      const staged = await apiService.importInventory(stageForm);

      const importId = staged?.data?.import_id;
      if (!importId) {
        throw new Error("Import did not return an import ID for preview.");
      }

      if (typeof onPreviewReady === "function") {
        await onPreviewReady({
          comparison: compareResult?.data || null,
          importResult: staged,
          importId,
          provider,
          category,
          fileName: selectedFile.name,
        });
        return;
      }

      // Fallback: stay in wizard preview if parent did not provide handoff
      setPreview(compareResult?.data || null);
      setStep(2);
    } catch (err) {
      setToast({
        message: err.message || "Failed to generate inventory preview",
        type: "error",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  const openReplaceConfirmModal = () => {
    setReplaceConfirmAcknowledged(false);
    setShowReplaceConfirmModal(true);
  };

  const closeReplaceConfirmModal = () => {
    if (replacing) return;
    setShowReplaceConfirmModal(false);
    setReplaceConfirmAcknowledged(false);
  };

  const handleReplace = async () => {
    if (!selectedFile) return;
    setReplacing(true);
    setShowReplaceConfirmModal(false);
    setStep(3);
    try {
      const formData = new FormData();
      formData.append("provider", provider);
      formData.append("category", category);
      formData.append("file", selectedFile);
      const result = await apiService.executeReplaceInventory(formData);
      setToast({
        message: result?.message || "Inventory replaced successfully",
        type: "success",
      });
      setTimeout(() => onComplete?.(result), 600);
    } catch (err) {
      setToast({
        message: err.message || "Failed to replace inventory",
        type: "error",
      });
      setStep(2);
    } finally {
      setReplacing(false);
      setReplaceConfirmAcknowledged(false);
    }
  };

  const blockingIssues = validation?.data?.blocking_issues || [];
  const validationNotices = validation?.data?.notices || [];
  const headers = validation?.data?.headers || {
    missing: [],
    matched: [],
    unused: [],
  };
  const fileValid = !!validation?.valid;

  return (
    <div className="min-h-screen pt-[80px] bg-[var(--ground,#f5f4f1)]">
      <div className="fixed top-4 right-4 z-[110]">
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: null, type: "success" })}
        />
      </div>

      <div className="w-full px-5 py-2 pb-14">
        <div className="mb-5 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[12px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
              Client Administration
            </p>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onBack}
                className="inline-flex h-8 w-8 items-center justify-center rounded-md text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                aria-label="Back"
              >
                ←
              </button>
              <h1 className="text-[26px] font-semibold leading-tight text-[var(--ink,#1c1c1a)]">
                {step === 1
                  ? "Import new inventory"
                  : step === 2
                    ? "Preview import"
                    : "Import"}
              </h1>
              {(provider || category || selectedFile?.name) ? (
                <p className="text-[14px] text-[var(--muted,#6b6a66)]">
                  {[provider, category, selectedFile?.name]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
              ) : null}
            </div>
          </div>
          <ImportReplaceStepper step={step} />
        </div>

        <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-white overflow-hidden relative">
          {step === 1 && (
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-5">
                  <div>
                    <label className="block text-[13px] font-medium text-[var(--ink,#1c1c1a)] mb-1.5">
                      Provider <span className="text-[var(--accent,#a51c1c)]">*</span>
                    </label>
                    <CustomSelect
                      value={provider}
                      onChange={(e) => {
                        const nextProvider = e.target.value;
                        setProvider(nextProvider);
                        const cats = PROVIDER_CATEGORY_MAPPING[nextProvider] || [];
                        setCategory(cats.length === 1 ? cats[0] : "");
                        clearFile();
                      }}
                      className="h-[38px] w-full rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white py-2 px-2.5 text-[13px] text-[var(--ink,#1c1c1a)] focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)]"
                    >
                      <option value="" disabled>
                        Select a provider
                      </option>
                      {providerOptions.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </CustomSelect>
                  </div>

                  <div>
                    <label className="block text-[13px] font-medium text-[var(--ink,#1c1c1a)] mb-1.5">
                      Category <span className="text-[var(--accent,#a51c1c)]">*</span>
                    </label>
                    <CustomSelect
                      value={category}
                      onChange={(e) => {
                        setCategory(e.target.value);
                        clearFile();
                      }}
                      disabled={!provider}
                      className="h-[38px] w-full rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white py-2 px-2.5 text-[13px] text-[var(--ink,#1c1c1a)] focus:outline-none focus:ring-2 focus:ring-[var(--muted,#6b6a66)]/20 focus:border-[var(--muted,#6b6a66)] disabled:bg-[var(--sunken,#eeece7)] disabled:text-[var(--muted,#6b6a66)]"
                    >
                      <option value="" disabled>
                        {provider
                          ? "Select a category"
                          : "Select a provider first"}
                      </option>
                      {availableCategories.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </CustomSelect>
                    {provider && availableCategories.length > 0 && (
                      <p className="mt-1.5 text-xs text-gray-500">
                        Only categories {provider} supplies are listed:{" "}
                        {availableCategories.join(", ")}.
                      </p>
                    )}
                  </div>

                  {provider && category && (
                    <div className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-[var(--sunken2,#fbfaf8)] p-4">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.05em] text-[var(--muted,#6b6a66)] mb-2">
                        EXPECTED FORMAT · {provider.toUpperCase()}{" "}
                        {category.toUpperCase()}
                      </div>
                      {formatLoading ? (
                        <p className="text-sm text-gray-500">Loading format…</p>
                      ) : formatInfo ? (
                        <>
                          <p className="text-sm text-gray-700 mb-3">
                            {formatInfo.description}
                          </p>
                          <div className="text-xs font-medium text-gray-600 mb-2">
                            Required headers
                          </div>
                          <div className="flex flex-wrap gap-1.5 mb-3">
                            {(formatInfo.required_headers || []).map((h) => (
                              <span
                                key={h}
                                className="inline-flex px-2 py-0.5 rounded bg-white border border-gray-200 text-xs text-gray-800"
                              >
                                {h}
                              </span>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={handleDownloadTemplate}
                            className="text-[13px] text-[var(--accent,#a51c1c)] hover:underline"
                          >
                            Download template (.xlsx)
                          </button>
                          {(formatInfo.optional_notes || []).length > 0 && (
                            <ul className="mt-3 space-y-1 text-xs text-slate-600 list-disc list-inside">
                              {formatInfo.optional_notes.map((note) => (
                                <li key={note}>{note}</li>
                              ))}
                            </ul>
                          )}
                        </>
                      ) : (
                        <p className="text-sm text-red-600">
                          No mapping is configured for this provider and
                          category.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="relative">
                    <div className="flex flex-col items-center justify-center px-6 py-14 border-2 border-dashed border-[var(--line-strong,#d5d3cc)] rounded-[10px] bg-[var(--sunken2,#fbfaf8)] hover:border-[var(--accent,#a51c1c)]/40 transition-colors">
                      <input
                        type="file"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        accept=".csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                        onChange={handleFileChange}
                        disabled={!provider || !category || validating}
                      />
                      <UploadCloudIcon className="w-12 h-12 text-[var(--faint,#9a9a95)] mb-2" />
                      <p className="text-[15px] text-[var(--ink,#1c1c1a)]">
                        <span className="text-[var(--accent,#a51c1c)] font-medium">
                          Click to upload
                        </span>{" "}
                        or drag and drop
                      </p>
                      <p className="text-[13px] text-[var(--muted,#6b6a66)] mt-1">
                        CSV, XLS, XLSX (max {MAX_FILE_SIZE_MB}MB)
                      </p>
                    </div>
                  </div>

                  {selectedFile && (
                    <div
                      className={`rounded-md border p-4 ${
                        fileValid
                          ? "border-green-200 bg-green-50"
                          : validating
                            ? "border-gray-200 bg-white"
                            : "border-red-200 bg-red-50"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-gray-900">
                            {selectedFile.name}
                          </p>
                          <p className="text-sm text-gray-600 mt-0.5">
                            {formatBytes(selectedFile.size)}
                            {validation?.data?.row_count != null
                              ? ` · ${validation.data.row_count.toLocaleString()} rows`
                              : ""}
                            {fileValid
                              ? ` · columns match ${provider} ${category}`
                              : ""}
                          </p>
                        </div>
                        {validating ? (
                          <Spinner className="w-5 h-5 text-[var(--accent,#a51c1c)]" />
                        ) : fileValid ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-600" />
                        ) : (
                          <AlertTriangleIcon className="w-5 h-5 text-red-600" />
                        )}
                      </div>
                      {fileValid && (
                        <p className="mt-2 text-sm text-green-700 font-medium">
                          Format recognized
                        </p>
                      )}
                    </div>
                  )}

                  {validationNotices.length > 0 && (
                    <div className="space-y-2.5">
                      {validationNotices.map((notice, idx) => (
                        <div
                          key={`${notice.code}-${idx}`}
                          className="rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3"
                        >
                          <p className="text-sm font-semibold text-amber-900">
                            {notice.title}
                          </p>
                          {notice.field && (
                            <p className="text-sm text-gray-800 mt-1">
                              <span className="text-gray-500">Field: </span>
                              <span className="font-medium">{notice.field}</span>
                            </p>
                          )}
                          {Array.isArray(notice.explanation_lines) &&
                            notice.explanation_lines.length > 0 && (
                              <>
                                <ul className="mt-1.5 space-y-0.5 list-disc list-inside text-sm text-gray-700">
                                  {notice.explanation_lines
                                    .slice(0, 10)
                                    .map((line) => (
                                      <li key={line}>{line}</li>
                                    ))}
                                </ul>
                                {(notice.count ?? 0) > 10 && (
                                  <p className="mt-1 text-xs text-gray-600">
                                    Showing 10 of {notice.count.toLocaleString()}.
                                  </p>
                                )}
                              </>
                            )}
                          {notice.how_to_fix && (
                            <p className="mt-2 text-xs text-gray-700 bg-white border border-amber-100 rounded px-2.5 py-1.5">
                              {notice.how_to_fix}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {validation && !fileValid && (
                    <div className="rounded-lg border border-red-200 bg-white p-4 space-y-4">
                      <div className="flex items-start gap-2">
                        <AlertTriangleIcon className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="text-sm font-semibold text-red-800">
                            This file cannot be imported yet
                          </h4>
                          <p className="text-sm text-gray-600 mt-0.5">
                            {validation.message}
                          </p>
                        </div>
                      </div>

                      <div className="space-y-2.5">
                        {blockingIssues.map((issue, idx) => {
                          const isBlank = issue.code === "required_field_blank";
                          const isDuplicate =
                            issue.code === "duplicate_line_numbers";
                          const isMissingHeaders =
                            issue.code === "missing_required_headers";

                          let detailLines = [];
                          if (isDuplicate && Array.isArray(issue.explanation_lines)) {
                            detailLines = issue.explanation_lines;
                          } else if (
                            isBlank &&
                            Array.isArray(issue.rows) &&
                            issue.rows.length > 0
                          ) {
                            detailLines = [
                              `Blank on row(s): ${issue.rows.slice(0, 60).join(", ")}${
                                issue.rows.length > 60
                                  ? ` (and ${issue.rows.length - 60} more)`
                                  : ""
                              }`,
                            ];
                          } else if (
                            isMissingHeaders &&
                            Array.isArray(issue.fields) &&
                            issue.fields.length > 0
                          ) {
                            detailLines = issue.fields;
                          } else if (
                            issue.explanation &&
                            !isBlank &&
                            !isDuplicate
                          ) {
                            detailLines = [issue.explanation];
                          } else if (issue.explanation && isBlank && !issue.field) {
                            detailLines = [issue.explanation];
                          }

                          return (
                            <div
                              key={`${issue.code}-${idx}`}
                              className="rounded-lg border border-red-200 bg-white overflow-hidden shadow-sm"
                            >
                              <div className="flex gap-0">
                                <div className="w-1 flex-shrink-0 bg-red-500" />
                                <div className="flex-1 px-3.5 py-3">
                                  <div className="flex items-start gap-2">
                                    <AlertTriangleIcon className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-semibold text-red-800">
                                        {issue.title}
                                      </p>
                                      {issue.field && (
                                        <p className="text-sm text-gray-800 mt-1">
                                          <span className="text-gray-500">
                                            Field:{" "}
                                          </span>
                                          <span className="font-medium">
                                            {issue.field}
                                          </span>
                                        </p>
                                      )}
                                      {detailLines.length > 0 && (
                                        <div className="mt-1.5 text-sm text-gray-700">
                                          {isDuplicate || isMissingHeaders ? (
                                            <ul className="space-y-0.5 list-disc list-inside">
                                              {detailLines.map((line) => (
                                                <li key={line}>{line}</li>
                                              ))}
                                            </ul>
                                          ) : (
                                            detailLines.map((line) => (
                                              <p key={line}>{line}</p>
                                            ))
                                          )}
                                        </div>
                                      )}
                                      {issue.how_to_fix && (
                                        <p className="mt-2 text-xs text-gray-600 bg-gray-50 border border-gray-100 rounded px-2.5 py-1.5">
                                          <span className="font-semibold text-gray-700">
                                            How to fix:{" "}
                                          </span>
                                          {issue.how_to_fix}
                                        </p>
                                      )}
                                      {issue.suggested_category && (
                                        <button
                                          type="button"
                                          className="mt-2 text-sm font-medium text-[var(--accent,#a51c1c)] hover:underline"
                                          onClick={() =>
                                            handleSwitchCategory(
                                              issue.suggested_category,
                                            )
                                          }
                                        >
                                          Switch category to{" "}
                                          {issue.suggested_category} and recheck
                                        </button>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {(headers.missing?.length > 0 ||
                        headers.matched?.length > 0 ||
                        headers.unused?.length > 0) && (
                        <details className="rounded-lg border border-gray-200 bg-gray-50 open:bg-white">
                          <summary className="cursor-pointer select-none px-3 py-2.5 text-sm font-medium text-gray-800 flex items-center justify-between gap-2">
                            <span>Header check summary</span>
                            <span className="text-xs font-normal text-gray-500">
                              Missing {headers.missing?.length || 0} · Matched{" "}
                              {headers.matched?.length || 0} · Not used{" "}
                              {headers.unused?.length || 0}
                            </span>
                          </summary>
                          <div className="px-3 pb-3 grid grid-cols-1 md:grid-cols-3 gap-2">
                            <HeaderChipPanel
                              title="Required missing"
                              tone="red"
                              items={headers.missing}
                            />
                            <HeaderChipPanel
                              title="Matched"
                              tone="green"
                              items={headers.matched}
                            />
                            <HeaderChipPanel
                              title="Not used"
                              tone="gray"
                              items={headers.unused}
                            />
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-between mt-8 pt-4 border-t border-[var(--line,#e4e2dc)]">
                <button
                  type="button"
                  onClick={clearFile}
                  disabled={!selectedFile}
                  className="px-4 py-2 border border-[var(--line-strong,#d5d3cc)] rounded-md text-[13px] text-[var(--ink,#1c1c1a)] bg-white hover:bg-[var(--sunken,#eeece7)] disabled:opacity-40"
                >
                  Remove file
                </button>
                <button
                  type="button"
                  onClick={handlePreview}
                  disabled={!fileValid || previewLoading || validating}
                  className="flex items-center justify-center gap-2 min-h-[42px] px-4 py-2.5 rounded-md bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] text-white text-[14px] font-semibold whitespace-nowrap disabled:opacity-40"
                >
                  {previewLoading ? (
                    <>
                      <Spinner className="w-4 h-4 text-white" />
                      Preparing preview…
                    </>
                  ) : (
                    "Preview import"
                  )}
                </button>
              </div>
            </div>
          )}

          <Loading
            show={step === 1 && validating}
            overlayMode="fixed"
            message="Validating file…"
            subMessage="Checking columns and format against the selected provider and category."
          />

          <Loading
            show={step === 1 && previewLoading}
            overlayMode="fixed"
            message="Preparing preview…"
            subMessage="Comparing against current inventory and loading records for review."
          />

          {step === 2 && preview && !onPreviewReady && (
            <div className="p-6 space-y-6">
              <div className="text-sm text-gray-600">
                {provider} · {category} · {selectedFile?.name}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 text-sm">
                <div className="rounded-md border border-gray-200 p-3">
                  <div className="text-xs text-gray-500">Current inventory</div>
                  <div className="text-xl font-semibold text-gray-900 mt-1">
                    {preview.current_count?.toLocaleString()}
                  </div>
                </div>
                <div className="rounded-md border border-gray-200 p-3">
                  <div className="text-xs text-gray-500">File count</div>
                  <div className="text-xl font-semibold text-gray-900 mt-1">
                    {(
                      preview.file_row_count ??
                      preview.file_count ??
                      0
                    ).toLocaleString()}
                  </div>
                  {(preview.counts?.skipped ?? 0) > 0 && (
                    <div className="text-[11px] text-gray-500 mt-0.5">
                      {(preview.file_count ?? 0).toLocaleString()} will import
                    </div>
                  )}
                </div>
                <div className="rounded border border-gray-200 p-3">
                  <div className="text-xs text-gray-500">Unchanged</div>
                  <div className="text-xl font-semibold text-gray-900 mt-1">
                    {preview.counts?.unchanged?.toLocaleString()}
                  </div>
                </div>
                <div className="rounded border border-blue-200 p-3">
                  <div className="text-xs text-blue-700">Changed</div>
                  <div className="text-xl font-semibold text-blue-800 mt-1">
                    {preview.counts?.changed?.toLocaleString()}
                  </div>
                </div>
                <div className="rounded border border-green-200 p-3">
                  <div className="text-xs text-green-700">New in file</div>
                  <div className="text-xl font-semibold text-green-800 mt-1">
                    {preview.counts?.added?.toLocaleString()}
                  </div>
                </div>
                <div className="rounded border border-red-200 p-3">
                  <div className="text-xs text-red-700">Leaving inventory</div>
                  <div className="text-xl font-semibold text-red-800 mt-1">
                    {preview.counts?.leaving?.toLocaleString()}
                  </div>
                </div>
              </div>

              <ReplaceComparisonGroupFilter comparison={preview} />

              <p className="text-sm text-gray-500">
                {preview.note ||
                  "Nothing changes in inventory until you confirm. Every import is logged and can be rolled back."}
              </p>

              <div className="flex justify-between pt-2 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="px-4 py-2 border border-[var(--line-strong,#d5d3cc)] rounded-md text-[13px] text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)]"
                >
                  Back
                </button>
                <button
                  type="button"
                  onClick={openReplaceConfirmModal}
                  disabled={replacing}
                  className="flex items-center justify-center gap-2 min-h-[42px] px-4 py-2.5 rounded-md bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] text-white text-[14px] font-semibold whitespace-nowrap disabled:opacity-40"
                >
                  {replacing ? (
                    <>
                      <Spinner className="w-4 h-4 text-white" />
                      Replacing…
                    </>
                  ) : (
                    "Replace inventory"
                  )}
                </button>
              </div>
            </div>
          )}

          {showReplaceConfirmModal && preview && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-[2px] p-4"
              onClick={closeReplaceConfirmModal}
            >
              <div
                className="bg-white rounded-[10px] shadow-xl max-w-md w-full overflow-hidden"
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="wizard-replace-confirm-title"
              >
                <div className="px-5 py-4 border-b border-[var(--line,#e4e2dc)]">
                  <h3
                    id="wizard-replace-confirm-title"
                    className="text-[16px] font-semibold text-[var(--ink,#1c1c1a)]"
                  >
                    Replace inventory?
                  </h3>
                  <p className="text-[13px] text-[var(--muted,#6b6a66)] mt-1">
                    The current {provider || "provider"}{" "}
                    {category || "category"} inventory set will be removed and
                    replaced with this file. A snapshot is kept in Import
                    history so you can roll back.
                  </p>
                </div>
                <div className="px-5 py-4 space-y-3">
                  <ul className="text-[13px] text-[var(--ink,#1c1c1a)] space-y-1.5">
                    <li>
                      <span className="font-semibold tabular-nums">
                        {(preview.current_count ?? 0).toLocaleString()}
                      </span>{" "}
                      current records will be removed
                    </li>
                    <li>
                      <span className="font-semibold tabular-nums">
                        {(preview.file_count ?? 0).toLocaleString()}
                      </span>{" "}
                      records from this file will be imported
                    </li>
                    {/* <li>
                      <span className="font-semibold tabular-nums">
                        {(preview.counts?.leaving ?? 0).toLocaleString()}
                      </span>{" "}
                      leaving inventory
                    </li> */}
                    <li>
                      <span className="font-semibold tabular-nums">
                        {(preview.counts?.added ?? 0).toLocaleString()}
                      </span>{" "}
                      new ·{" "}
                      <span className="font-semibold tabular-nums">
                        {(preview.counts?.changed ?? 0).toLocaleString()}
                      </span>{" "}
                      changed ·{" "}
                      <span className="font-semibold tabular-nums">
                        {(preview.counts?.unchanged ?? 0).toLocaleString()}
                      </span>{" "}
                      unchanged
                    </li>
                  </ul>
                  <label className="flex items-start gap-2 text-[13px] text-[var(--ink,#1c1c1a)]">
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={replaceConfirmAcknowledged}
                      onChange={(e) =>
                        setReplaceConfirmAcknowledged(e.target.checked)
                      }
                      disabled={replacing}
                    />
                    <span>
                      I understand the current {provider || "provider"}{" "}
                      {category || "category"} inventory will be removed and
                      replaced with this file.
                    </span>
                  </label>
                </div>
                <div className="px-5 py-4 border-t border-[var(--line,#e4e2dc)] flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeReplaceConfirmModal}
                    disabled={replacing}
                    className="px-4 py-2 border border-[var(--line-strong,#d5d3cc)] rounded-md text-[13px] text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleReplace}
                    disabled={!replaceConfirmAcknowledged || replacing}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-[13px] font-medium text-white bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {replacing ? (
                      <>
                        <Spinner className="w-4 h-4 text-white" />
                        Replacing…
                      </>
                    ) : (
                      "Confirm replace"
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="p-16 flex flex-col items-center justify-center">
              <Spinner className="w-10 h-10 text-[var(--accent,#a51c1c)] mb-4" />
              <p className="text-base font-medium text-gray-900">
                Replacing inventory…
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Saving a snapshot, removing the current {provider} {category}{" "}
                set, and loading the new file.
              </p>
            </div>
          )}

          <Loading
            show={replacing || step === 3}
            overlayMode="fixed"
            message="Replacing inventory…"
            subMessage={`Saving a snapshot, removing the current ${provider || ""} ${category || ""} set, and loading the new file.`}
          />
        </div>
      </div>
    </div>
  );
}
